// Content for the in-app "Help & tips" page (pages/Help.jsx). Plain data so it's easy to edit
// without touching layout code. Each article can be deep-linked as /help#<id>.
// Keep it short and friendly; keep it TRUE to how the app actually behaves (see CLAUDE.md).
//
// Fields: id, section, title, icon (key of ICONS in Help.jsx), color, pro (Pro/Family only),
//         link {to, label}, summary (one line), points (short bullets), tip (optional), example (optional)

export const SECTIONS = [
    { id: "start", label: "Getting started" },
    { id: "features", label: "Features" },
    { id: "ai", label: "AI assistants" },
    { id: "tips", label: "Money tips" },
    { id: "plans", label: "Plans & family" },
];

export const ARTICLES = [
    // ---------------- Getting started ----------------
    {
        id: "first-week", section: "start", title: "Your first 10 minutes", icon: "rocket", color: "#4F46E5",
        summary: "Three small steps that make everything else in BudgetWise useful.",
        points: [
            "Add the bills and subscriptions you already know about (rent, phone, internet, Netflix…).",
            "Log a few recent expenses — the + button works from any page.",
            "Set a budget for your top 2–3 spending categories (often Groceries, Food & Drink, Shopping).",
        ],
        tip: "Don't try to be perfect on day one. A rough picture after one week beats a perfect one you never finish.",
    },
    {
        id: "quick-add", section: "start", title: "Quick Add (the + button)", icon: "plus", color: "#4F46E5",
        summary: "Add an expense, income, subscription or savings fund from anywhere in the app.",
        points: [
            "Tap the + button, pick a tab, fill in the basics and save.",
            "On Pro and Family you can tap Speak and just say it: \"$20 for dinner at Chipotle yesterday\". It fills in the form for you to check.",
            "Prefer screenshots? Connect an AI assistant (see AI assistants below) and share receipts there.",
        ],
    },

    // ---------------- Features ----------------
    {
        id: "dashboard", section: "features", title: "Dashboard", icon: "dashboard", color: "#4F46E5",
        link: { to: "/dashboard", label: "Open Dashboard" },
        summary: "Your money at a glance for this week, month, year or a custom range.",
        points: [
            "Income vs. spending chart, spending by category, and recent transactions.",
            "Upcoming renewals (subscriptions) and Upcoming bills (recurring expenses) due in the next 15 days.",
            "Budget and savings progress so you can spot problems early.",
            "If a bill is overdue, you'll be asked \"Did you pay this bill?\" — one tap records it.",
        ],
    },
    {
        id: "expenses", section: "features", title: "Expenses & income", icon: "receipt", color: "#EF4444",
        link: { to: "/expenses", label: "Open Expenses" },
        summary: "Everything you spend and earn, in one searchable list.",
        points: [
            "Search, filter by type or category, sort by date or amount, and switch between table and card views.",
            "Export to CSV any time for your own spreadsheets.",
            "Each entry has a category — those categories power your budgets and charts.",
        ],
    },
    {
        id: "recurring-bills", section: "features", title: "Recurring bills", icon: "repeat", color: "#EF4444",
        link: { to: "/expenses", label: "Add a bill" },
        summary: "Rent, mortgage, phone, electricity — bills that come back but aren't subscriptions.",
        points: [
            "When adding or editing an expense, tick Recurring, choose how often it repeats and set the next due date.",
            "It then shows in Upcoming bills on the Dashboard as the due date gets close.",
            "Tap Mark paid when you pay it: BudgetWise records the payment and moves the due date to the next cycle.",
        ],
        tip: "Use recurring bills for things that count toward your spending and budgets. Use Subscriptions for services you might cancel.",
    },
    {
        id: "subscriptions", section: "features", title: "Subscriptions", icon: "refresh", color: "#8B5CF6",
        link: { to: "/subscriptions", label: "Open Subscriptions" },
        summary: "Track streaming, apps, gym and other memberships — and never miss a renewal.",
        points: [
            "Upcoming shows what renews in the next 15 days; History shows past and cancelled ones.",
            "Auto: your card is charged automatically, so dates roll forward on their own.",
            "Manual: you pay it yourself — tap ✓ when paid and it moves to the next billing date.",
            "Mark free trials as Trial so you remember to cancel before they convert.",
            "Cancelled something? Set it to Inactive to keep the history without counting it.",
        ],
    },
    {
        id: "budgets", section: "features", title: "Budgets", icon: "gauge", color: "#F59E0B",
        link: { to: "/budgets", label: "Open Budgets" },
        summary: "A spending limit per category, weekly or monthly.",
        points: [
            "Pick a category, choose weekly (Mon–Sun) or monthly, and set a limit.",
            "Every expense in that category counts toward it automatically; the bar shows what's left.",
            "Budgets reset at the start of each period — unused money doesn't roll over.",
            "Subscriptions aren't counted, only expenses.",
        ],
        tip: "Start with the one or two categories where you overspend most. You can always add more.",
    },
    {
        id: "sinking-funds", section: "features", title: "Sinking funds", icon: "target", color: "#EC4899", pro: true,
        link: { to: "/funds", label: "Open Sinking Funds" },
        summary: "Save a little at a time for a big expense you know is coming.",
        points: [
            "A sinking fund is money you set aside regularly for a planned cost — car insurance, holidays, a new laptop, festival gifts, school fees.",
            "Create a fund with a goal amount and (optionally) a due date, then record deposits as you save.",
            "Had to dip into it? Record a withdrawal — the progress ring and history stay accurate.",
            "Due dates also appear on your Calendar.",
        ],
        example: "Car insurance of $1,200 due in 12 months → save $100 a month. When the bill arrives, the money is already there.",
        tip: "Sinking funds turn \"surprise\" expenses into planned ones — the expenses were never really surprises, just forgotten.",
    },
    {
        id: "investments", section: "features", title: "Investments", icon: "candles", color: "#10B981", pro: true,
        link: { to: "/investments", label: "Open Investments" },
        summary: "Follow your brokerage, retirement, crypto and savings balances over time.",
        points: [
            "Add each account (brokerage, 401k, IRA, HSA, crypto, savings…).",
            "Every so often, log its current total value — copy it from your bank or broker's app.",
            "BudgetWise draws the trend and shows your change since you started tracking.",
            "These are balance snapshots, not individual trades — just the total on that date.",
        ],
        tip: "Update on the same day each month (e.g. the 1st) for a clean, comparable chart.",
    },
    {
        id: "transactions", section: "features", title: "Transactions", icon: "arrows", color: "#0EA5E9",
        link: { to: "/transactions", label: "Open Transactions" },
        summary: "A day-by-day timeline of everything in and out.",
        points: ["Grouped by date with each day's net total, plus search and income/expense filters."],
    },
    {
        id: "calendar", section: "features", title: "Calendar", icon: "calendar", color: "#14B8A6",
        link: { to: "/calendar", label: "Open Calendar" },
        summary: "See subscription renewals and fund due dates on a monthly calendar.",
        points: ["Switch between the month grid and an agenda list. Great for planning around paydays."],
    },
    {
        id: "analytics", section: "features", title: "Analytics", icon: "chart", color: "#F97316", pro: true,
        link: { to: "/analytics", label: "Open Analytics" },
        summary: "Where your money goes over the last six months.",
        points: ["Category trends month by month, your top categories, and your subscription mix."],
    },
    {
        id: "tools", section: "features", title: "Tools", icon: "calculator", color: "#0D9488",
        link: { to: "/tools", label: "Open Tools" },
        summary: "Handy calculators for planning.",
        points: [
            "Investment growth — see what regular saving could grow to.",
            "50/30/20 budget — split your income into needs, wants and savings.",
            "Net worth — add up what you own minus what you owe.",
            "Mortgage — estimate monthly payments.",
        ],
    },
    {
        id: "categories-settings", section: "features", title: "Categories & settings", icon: "settings", color: "#64748B",
        link: { to: "/settings", label: "Open Settings" },
        summary: "Make BudgetWise yours.",
        points: [
            "Add your own categories with an icon (e.g. Pets, Kids, Insurance) under Settings → Preferences.",
            "Choose light/dark mode, an accent color and your currency.",
            "Pro and Family can export all their data to Excel from Settings.",
        ],
    },

    // ---------------- AI assistants ----------------
    {
        id: "ai-what", section: "ai", title: "Use BudgetWise from Claude, ChatGPT or Gemini", icon: "bot", color: "#4F46E5", pro: true,
        link: { to: "/settings?tab=ai", label: "Connect an assistant" },
        summary: "Share a screenshot or just ask — your assistant updates BudgetWise for you.",
        points: [
            "Go to Settings → AI assistants, copy the connector URL and follow the steps for Claude, ChatGPT or Gemini.",
            "In Gemini, type @ and pick BudgetWise in your message so Gemini knows to use it.",
            "Sign in and tap Allow. That's it — you can disconnect any time from the same page.",
            "Your assistant can add and edit expenses, subscriptions, funds, budgets, investments and categories, answer questions about your spending, and draw charts of your trends.",
            "It can't delete anything or change your account, plan or household.",
        ],
    },
    {
        id: "ai-examples", section: "ai", title: "Things to try", icon: "sparkles", color: "#4F46E5", pro: true,
        summary: "Copy any of these into your assistant once it's connected.",
        points: [
            "📸 (attach a receipt) \"Add this to BudgetWise.\"",
            "📸 (attach a bill history page) \"Add all of these electricity bills.\"",
            "\"Add my Netflix subscription, $15.49 a month, renews on the 1st.\"",
            "\"Put $200 into my Japan trip fund.\"",
            "\"My Fidelity 401k is worth $12,400 today — log it.\"",
            "\"What's my available balance?\"",
            "\"Any upcoming renewals or bills this week? Anything overdue?\"",
            "\"I paid the rent — mark it as paid.\"",
            "\"How much did I spend on groceries last month?\"",
            "\"Chart my spending by category for the last 6 months.\"",
            "\"Show income vs. expenses for this year as a chart.\"",
            "\"Set a $400 monthly budget for Food & Drink.\"",
            "\"Change last Friday's Uber to $18.50.\"",
        ],
        tip: "Sharing the same screenshot twice is safe — entries that match an existing one (same amount, date and merchant) are skipped, and your assistant tells you which.",
    },

    // ---------------- Money tips ----------------
    {
        id: "tip-pay-yourself", section: "tips", title: "Pay yourself first", icon: "piggy", color: "#10B981",
        summary: "Move money to savings on payday, before you start spending.",
        points: [
            "Decide an amount, move it as soon as you're paid, and treat it like a bill.",
            "Record it as a deposit into a sinking fund so you can see it grow.",
        ],
    },
    {
        id: "tip-503020", section: "tips", title: "Try the 50/30/20 starting point", icon: "pie", color: "#F59E0B",
        link: { to: "/tools", label: "Open the 50/30/20 calculator" },
        summary: "A simple first split of your take-home pay.",
        points: [
            "About 50% for needs (housing, bills, groceries), 30% for wants, 20% for savings and paying off debt.",
            "It's a starting point, not a rule — adjust it to your life, then set budgets to match.",
        ],
    },
    {
        id: "tip-emergency", section: "tips", title: "Build an emergency fund", icon: "shield", color: "#0EA5E9",
        summary: "A cushion for the things you can't plan for.",
        points: [
            "A common goal is 3–6 months of essential expenses. Start with one month — it already helps a lot.",
            "Create it as a sinking fund called \"Emergency\" with no due date.",
        ],
    },
    {
        id: "tip-sinking-math", section: "tips", title: "Size a sinking fund in 10 seconds", icon: "target", color: "#EC4899",
        summary: "Goal ÷ months left = what to save each month.",
        points: [
            "$900 holiday in 6 months → $150 a month.",
            "List the yearly costs that always \"surprise\" you (insurance, car service, gifts, renewals) and give each one a fund.",
        ],
    },
    {
        id: "tip-subscriptions", section: "tips", title: "Do a monthly subscription check", icon: "refresh", color: "#8B5CF6",
        summary: "Small monthly charges add up quietly.",
        points: [
            "Once a month, scan Subscriptions → Upcoming. Haven't used it in a month? Consider cancelling.",
            "Tag free trials and set the renewal date to the day the trial ends, so it shows up before you're charged.",
        ],
    },
    {
        id: "tip-weekly", section: "tips", title: "The 10-minute weekly check-in", icon: "calendar", color: "#14B8A6",
        summary: "A short habit beats a long monthly session.",
        points: [
            "Log anything you missed, glance at your budgets, and check what's due on the Calendar.",
            "Pick the same time each week — Sunday evening works for a lot of people.",
        ],
    },

    // ---------------- Plans & family ----------------
    {
        id: "plans", section: "plans", title: "Free, Pro and Family", icon: "crown", color: "#F59E0B",
        link: { to: "/pricing", label: "Compare plans" },
        summary: "What each plan includes.",
        points: [
            "Free: unlimited expenses & income, recurring bills, subscriptions, budgets, transactions, calendar and tools.",
            "Pro: everything in Free, plus Sinking Funds, Investments, Analytics, voice entry, AI assistants and data export.",
            "Family: everything in Pro, plus sharing BudgetWise with up to 5 people in your household.",
        ],
    },
    {
        id: "family", section: "plans", title: "Sharing with your family", icon: "users", color: "#4F46E5",
        link: { to: "/settings?tab=household", label: "Set up your household" },
        summary: "Everyone gets their own login, and you see the full picture together.",
        points: [
            "The Family plan owner creates a household in Settings → Family and invites people by email or with an invite code.",
            "Members get all Pro features through the owner's plan — they don't need to pay separately.",
            "Shared by everyone: expenses, budgets and categories. Anyone can add or edit them.",
            "Personal but visible: subscriptions, sinking funds and investments. Everyone can see them; only the person who created one can change it.",
            "Use the Combined / Just me toggle to switch between the household view and your own.",
        ],
    },
];
